<template>
  <div class="collapsible-menu-item my-1">
    <eg-button
      v-b-tooltip.hover.right="tooltip"
      class="menu-item-button w-100 align-items-center collapse-control"
      :class="[{ collapsed: !visible }, buttonClass]"
      :aria-expanded="visible ? 'true' : 'false'"
      :aria-controls="`collapse-${_uid}`"
      type="ordinary"
      :icon="icon"
      :right-icon="isMenuHided ? null : visible ? 'chevron-up' : 'chevron-down'"
      icon-size="medium"
      @click="handleToggle"
    >
      <transition name="sidebar-maximized-transition">
        <template v-if="!isMenuHided">
          <span class="flex-grow-1 text-left">{{ label }}</span>
        </template>
      </transition>
    </eg-button>

    <eg-b-collapse :id="`collapse-${_uid}`" :visible="visible" @input="onInput">
      <div class="submenu-items">
        <slot />
      </div>
    </eg-b-collapse>
  </div>
</template>

<script>
export default {
  name: "EgCollapsibleItem",

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      required: true,
    },
    icon: {
      type: String,
      required: true,
    },
    isMenuHided: {
      type: Boolean,
      default: false,
    },
    initialExpanded: {
      type: Boolean,
      default: false,
    },
    tooltip: {
      type: String,
      default: "",
    },
    buttonClass: {
      type: [String, Array, Object],
      default: null,
    },
  },

  data() {
    return {
      visible: this.initialExpanded,
    };
  },

  watch: {
    value: {
      handler(newVal) {
        this.visible = newVal;
      },
      immediate: true,
    },
    initialExpanded: {
      handler(newVal) {
        this.visible = newVal;
      },
      immediate: true,
    },
  },

  methods: {
    open() {
      this.visible = true;
      this.$emit("toggle", true);
    },

    close() {
      this.visible = false;
      this.$emit("toggle", false);
    },

    handleToggle() {
      this.visible = !this.visible;
      this.$emit("toggle", this.visible);
    },

    onInput(value) {
      this.visible = value;
      this.$emit("input", value);
    },
  },
};
</script>

<style scoped lang="scss">
.collapsible-menu-item {
  padding: 0 20px;
  @media (max-height: 800px) {
    margin: 0 !important;
  }
  .collapsible-menu-item {
    padding: 0 0 0 12px;
    @media (max-height: 800px) {
      margin: 0 !important;
    }
  }
  .menu-item-button {
    /* Override EgButton defaults for sidebar menu */
    padding: 0 12px;
    border-radius: 12px;
    height: 40px;
    width: 100%;
    justify-content: start;
    font-family: Inter, sans-serif;
    /* Default colors and font weight */
    color: var(--color-text-neutral-on-white);
    font-weight: 400;

    ::v-deep .eg-icon,
    ::v-deep .icon .eg-icon {
      color: var(--color-icon-neutral-neutral);
    }

    /* Position right icon at the right edge */
    ::v-deep .icon-right {
      margin-left: auto;
    }

    /* Hover and focus-visible states */
    &:hover:not(:disabled),
    &:focus-visible:not(:disabled) {
      background-color: var(--color-surface-neutral-hover);
      color: var(--color-text-neutral-on-white);

      ::v-deep .eg-icon,
      ::v-deep .icon .eg-icon {
        color: var(--color-icon-primary-primary) !important;
      }

      ::v-deep .icon-right .eg-icon {
        color: var(--color-icon-primary-primary) !important;
      }
    }

    /* Press state */
    &:active {
      background-color: var(--color-surface-neutral-press);
      color: var(--color-text-neutral-on-white);

      ::v-deep .eg-icon,
      ::v-deep .icon .eg-icon {
        color: var(--color-icon-primary-primary) !important;
      }

      ::v-deep .icon-right .eg-icon {
        color: var(--color-icon-primary-primary) !important;
      }
    }

    span {
      font-size: 0.875rem;
      line-height: 1.125rem;
      margin-top: 2px;
    }
  }

  .submenu-items {
    margin-left: 0;
    margin-top: 8px;
    padding-left: 0;
    @media (max-height: 800px) {
      margin: 0 !important;
    }
    // Style submenu items to match the design
    ::v-deep .sidebar-menu-item {
      padding: 0;
      margin: 4px 0;

      .menu-item-button {
        padding-left: 40px;
        border-radius: 12px;
        height: 32px;
        width: 100%;
        justify-content: start;
        font-family: Inter, sans-serif;
        color: var(--color-text-neutral-on-white);
        font-weight: 400;
        background-color: transparent;
        font-size: var(--typography-font-size-fs-200);

        // Active state styling for submenu items
        &.router-link-exact-active,
        &.eg-new-button--active {
          background-color: #e8e2ff; // Light purple background as shown in design
          color: var(--color-text-neutral-on-white);

          ::v-deep .eg-icon,
          ::v-deep .icon .eg-icon {
            color: var(--color-icon-primary-primary) !important;
          }
        }

        // Hover state for submenu items
        &:hover:not(:disabled),
        &:focus-visible:not(:disabled) {
          background-color: var(--color-surface-neutral-hover);
          color: var(--color-text-neutral-on-white);
        }

        // Press state for submenu items
        &:active {
          background-color: var(--color-surface-neutral-press);
          color: var(--color-text-neutral-on-white);
        }

        ::v-deep .eg-icon,
        ::v-deep .icon .eg-icon {
          color: var(--color-icon-neutral-neutral);
        }

        span {
          font-size: 0.875rem;
          line-height: 1.125rem;
          margin-top: 2px;
        }
      }
    }
  }
}
</style>

<style lang="scss">
// When menu is hidden, adjust styling (unscoped to work globally)
.is-sidebar-minimized .sidebar-wrapper {
  .collapsible-menu-item {
    padding: 0;

    .menu-item-button {
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      min-width: 40px;

      .button-text {
        display: none !important;
      }
    }
    .collapse-control.menu-item-button {
      margin: 0 8px;
      max-width: 48px;
    }
    .submenu-items {
      margin-left: 0;
      padding-left: 0;
      border-left: none;

      .sidebar-menu-item {
        padding: 0 8px;

        .menu-item-button {
          padding: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          min-width: 40px;

          .button-text {
            display: none !important;
          }
        }
      }
    }
  }
}
</style>
